/**
 * Custom replacement for native `<select>`. Matches the dashboard's
 * rectangular aesthetic (square borders, CSS-var palette, uppercase
 * micro-labels) and mirrors ColumnPicker's popover mechanics:
 *   - Fixed-position menu anchored to trigger via getBoundingClientRect
 *   - Closes on outside click / Escape
 *   - Keyboard: Enter/Space/Arrow opens, Up/Down navigates, Enter selects
 *
 * Variants:
 *   field  – form-field sized (default; CommandLog/TraceLog/UserManagement)
 *   chip   – small uppercase pill (CommandLog context window)
 *   bare   – no border, inline uppercase (ChatConsole thinking level)
 *   inline – baseline-aligned prose dropdown (ContrastPanel DimSelect)
 *
 * `options` is `[{ value, label }]`. Labels can be any ReactNode; the
 * trigger looks the label up by value so the rendered text always
 * reflects the current selection.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const TRIGGER_VARIANTS = {
    field:
        "min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] hover:border-[color:var(--line-strong)]",
    chip:
        "rounded-[2px] border border-[color:var(--line)] bg-transparent px-[6px] py-[2px] text-[10px] leading-[1.2] tracking-[0.04em] uppercase text-[color:var(--muted)] hover:text-[color:var(--ink)]",
    bare:
        "border-0 bg-transparent text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)] hover:text-[color:var(--ink)]",
    inline: null, // ContrastPanel-style — caller controls wrapping/typography
};

// Menu items mirror the trigger's typographic feel so the dropdown reads as
// a continuation of the control rather than a generic listbox. `bare` and
// `inline` triggers sit inside uppercase contexts (Think label, ContrastPanel
// toolbar) — their menus need the same casing or the rows look out of place.
const MENU_ITEM_VARIANTS = {
    field: "text-[12px] leading-[1.2]",
    chip: "text-[10px] leading-[1.2] tracking-[0.04em] uppercase",
    bare: "text-[10px] leading-[1.2] tracking-[0.14em] uppercase",
    inline: "text-[11px] leading-[1.2] uppercase tracking-[0.08em]",
};

const CARET = (
    <span aria-hidden="true" className="pointer-events-none ml-[6px] text-[9px] leading-none text-[color:var(--muted-soft)]">
        ▾
    </span>
);

// Step to the next selectable option in `dir` (+1/-1), skipping any
// `disabled` entries and wrapping around. Returns `from` unchanged when
// every option is disabled, so navigation can't get stuck or land on a
// non-pickable row. Exported for unit tests (the repo has no DOM-render
// harness, so the branchy nav logic is tested as a pure function).
export function nextEnabledIndex(options, from, dir) {
    const n = options.length;
    if (!n) return -1;
    let i = from;
    for (let k = 0; k < n; k++) {
        i = (i + dir + n) % n;
        if (!options[i]?.disabled) return i;
    }
    return from;
}

export default function Select({
    value,
    onChange,
    options,
    variant = "field",
    className = "",
    ariaLabel,
    title,
    disabled = false,
    placeholder = "",
    menuWidth, // optional override; defaults to trigger width
    align = "left", // "left" | "right" — preferred horizontal anchor.
                    // Auto-flips to the opposite edge if the preferred
                    // side would overflow the viewport, so callers near
                    // a boundary still get a visible menu.
    triggerStyle, // optional inline style escape hatch for compact rows
    triggerContent, // optional node to render INSTEAD of the label+caret —
                    // lets a caller use a fixed-size icon trigger (e.g. the
                    // thinking-depth meter) while the menu still lists the
                    // text options. Caller supplies the full trigger
                    // styling via `className`.
}) {
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [layoutTick, setLayoutTick] = useState(0); // bumps on resize/scroll so menu re-measures
    const [menuSize, setMenuSize] = useState(null); // measured after first paint; null forces a pre-measure pass
    const btnRef = useRef(null);
    const popRef = useRef(null);

    const selectedIndex = useMemo(
        () => options.findIndex((o) => o.value === value),
        [options, value],
    );
    const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

    useEffect(() => {
        if (!open) return;
        const bump = () => setLayoutTick((n) => n + 1);
        window.addEventListener("resize", bump);
        window.addEventListener("scroll", bump, true);
        return () => {
            window.removeEventListener("resize", bump);
            window.removeEventListener("scroll", bump, true);
        };
    }, [open]);

    // Boundary-aware positioning. Two-pass: first render uses the trigger
    // width as an estimate and renders the menu hidden so we can measure
    // its real content size; second pass uses that size to decide which
    // edge to anchor against and whether to drop-up.
    //
    // We can't precompute overflow from `triggerWidth` alone — the menu's
    // intrinsic content (e.g. THINK levels: LOW/MEDIUM/HIGH/XHIGH) can be
    // wider than the trigger because we set `minWidth`, not `width`. So
    // measuring the rendered menu is the only way to know its true bbox.
    const menuStyle = (() => {
        if (!open || !btnRef.current) return null;
        const r = btnRef.current.getBoundingClientRect();
        const triggerWidth = Math.round(r.width);
        const margin = 4;
        const edgePad = 4;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // Width/height used for overflow math: prefer measured values,
        // fall back to trigger width / 0 (forces the first pass to render
        // somewhere reasonable so measurement can happen).
        const w = menuSize?.width ?? Math.max(menuWidth ?? triggerWidth, triggerWidth);
        const h = menuSize?.height ?? 0;

        const spaceBelow = vh - r.bottom - margin - edgePad;
        const spaceAbove = r.top - margin - edgePad;
        // Drop-up when below can't fit the menu (or any reasonable chunk
        // of it) and above has more room.
        const dropUp = h > 0
            ? spaceBelow < h && spaceAbove > spaceBelow
            : spaceBelow < 160 && spaceAbove > spaceBelow;
        const maxHeight = Math.max(80, dropUp ? spaceAbove : spaceBelow);

        const base = { minWidth: menuWidth ?? triggerWidth, maxHeight: `${maxHeight}px` };
        if (dropUp) base.bottom = vh - r.top + margin;
        else base.top = r.bottom + margin;

        // Horizontal anchor: prefer caller's `align`, flip if it overflows.
        const leftAnchorOverflow = r.left + w > vw - edgePad;
        const rightAnchorOverflow = r.right - w < edgePad;
        const useRight = align === "right" ? !rightAnchorOverflow : leftAnchorOverflow && !rightAnchorOverflow;
        if (useRight) {
            base.right = Math.max(edgePad, vw - r.right);
        } else {
            // Clamp so a menu wider than the available space still sits inside.
            const left = Math.min(r.left, vw - w - edgePad);
            base.left = Math.max(edgePad, left);
        }

        // Hide the menu on the first pass (before we've measured it) to
        // avoid a one-frame flash at an incorrect position. `visibility`
        // keeps it laid out so the measurement effect can read its size.
        if (menuSize === null) base.visibility = "hidden";

        return base;
    })();

    // Reset measurement when the menu closes so the next open starts a
    // fresh two-pass measurement (first pass hidden, second pass anchored
    // from the measured size).
    useEffect(() => {
        if (!open) setMenuSize(null);
    }, [open]);

    // Measure the menu after layout. Re-runs on layoutTick (resize/scroll)
    // and when options change. The equality check prevents render loops
    // when the measured size hasn't actually changed.
    useLayoutEffect(() => {
        if (!open || !popRef.current) return;
        const rect = popRef.current.getBoundingClientRect();
        setMenuSize((prev) => {
            if (prev && Math.abs(prev.width - rect.width) < 0.5 && Math.abs(prev.height - rect.height) < 0.5) {
                return prev;
            }
            return { width: rect.width, height: rect.height };
        });
    }, [open, layoutTick, options]);

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (popRef.current?.contains(e.target)) return;
            if (btnRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === "Escape") {
                setOpen(false);
                btnRef.current?.focus();
            }
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    useEffect(() => {
        if (open) {
            setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
        }
    }, [open, selectedIndex]);

    const handleTriggerKey = useCallback(
        (e) => {
            if (disabled) return;
            if (!open) {
                if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown" || e.key === "ArrowUp") {
                    e.preventDefault();
                    setOpen(true);
                }
                return;
            }
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveIndex((i) => nextEnabledIndex(options, i, 1));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveIndex((i) => nextEnabledIndex(options, i, -1));
            } else if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                const next = options[activeIndex];
                if (next && !next.disabled) {
                    onChange(next.value);
                    setOpen(false);
                    btnRef.current?.focus();
                }
            } else if (e.key === "Tab") {
                setOpen(false);
            }
        },
        [open, disabled, options, activeIndex, onChange],
    );

    const triggerVariantClass = TRIGGER_VARIANTS[variant] ?? TRIGGER_VARIANTS.field;
    // Only the field variant fills its parent — chip/bare are intrinsic-width
    // pills that sit inside flex rows next to other compact controls.
    const widthClass = variant === "field" ? "w-full" : "";
    const baseTriggerClass = variant === "inline"
        ? null
        : [
            "inline-flex items-center justify-between gap-[6px] cursor-pointer outline-none transition-colors",
            widthClass,
            triggerVariantClass,
            disabled ? "cursor-not-allowed opacity-50" : "",
            className,
        ].filter(Boolean).join(" ");

    const labelNode = selected ? selected.label : (
        <span className="text-[color:var(--muted-soft)]">{placeholder}</span>
    );

    // Inline variant mimics prose: baseline-aligned, dotted underline,
    // small caret. Visually identical to ContrastPanel's old DimSelect
    // but driven by the same popover machinery as the other variants.
    if (variant === "inline") {
        return (
            <>
                <button
                    ref={btnRef}
                    type="button"
                    onClick={() => !disabled && setOpen((v) => !v)}
                    onKeyDown={handleTriggerKey}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-label={ariaLabel}
                    title={title}
                    disabled={disabled}
                    className={[
                        // `<button>` doesn't inherit text-transform in some
                        // browsers; force uppercase here so the trigger
                        // matches the uppercase prose context (e.g. the
                        // ContrastPanel toolbar's ROWS:/COLS: row).
                        "relative inline-flex items-baseline cursor-pointer border-0 bg-transparent p-0 text-left text-inherit uppercase border-b border-dotted border-[color:var(--line-strong)] hover:text-[color:var(--ink)] outline-none",
                        disabled ? "cursor-not-allowed opacity-50" : "",
                        className,
                    ].filter(Boolean).join(" ")}
                >
                    <span>{labelNode}</span>
                    <span aria-hidden="true" className="pl-[3px] text-[9px] leading-none text-[color:var(--muted-soft)]">▾</span>
                </button>
                {menuStyle ? <Menu
                    refProp={popRef}
                    style={menuStyle}
                    itemClass={MENU_ITEM_VARIANTS.inline}
                    options={options}
                    activeIndex={activeIndex}
                    setActiveIndex={setActiveIndex}
                    value={value}
                    onPick={(v) => { onChange(v); setOpen(false); btnRef.current?.focus(); }}
                /> : null}
            </>
        );
    }

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                onClick={() => !disabled && setOpen((v) => !v)}
                onKeyDown={handleTriggerKey}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
                title={title}
                disabled={disabled}
                className={triggerContent != null
                    ? ["cursor-pointer outline-none transition-colors", disabled ? "cursor-not-allowed opacity-50" : "", className].filter(Boolean).join(" ")
                    : baseTriggerClass}
                style={triggerStyle}
            >
                {triggerContent != null ? triggerContent : (
                    <>
                        <span className="truncate text-left">{labelNode}</span>
                        {CARET}
                    </>
                )}
            </button>
            {menuStyle ? <Menu
                refProp={popRef}
                style={menuStyle}
                itemClass={MENU_ITEM_VARIANTS[variant] ?? MENU_ITEM_VARIANTS.field}
                options={options}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                value={value}
                onPick={(v) => { onChange(v); setOpen(false); btnRef.current?.focus(); }}
            /> : null}
        </>
    );
}

function Menu({ refProp, style, itemClass, options, activeIndex, setActiveIndex, value, onPick }) {
    // Portal to <body> so `position: fixed` is anchored to the viewport.
    // The dashboard's <aside> wrappers use `contain: content`, which
    // creates a new containing block — without the portal, our `right`
    // anchor would be measured from the sidebar's edge, not the window's,
    // and the menu would render off-screen.
    return createPortal(
        <div
            ref={refProp}
            role="listbox"
            className="fixed z-[10010] overflow-y-auto border border-[color:var(--line-strong)] bg-[color:var(--bg)] shadow-lg"
            style={style}
        >
            <div className="flex flex-col p-[3px]">
                {options.map((opt, i) => {
                    const isSelected = opt.value === value;
                    const isActive = i === activeIndex;
                    // Disabled options stay visible but unpickable — used to
                    // show a level a model can't accept (e.g. "off" on Fable,
                    // which 400s) with a `title` saying why, rather than
                    // hiding it and leaving the gap unexplained.
                    const isDisabled = !!opt.disabled;
                    return (
                        <button
                            key={String(opt.value)}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            aria-disabled={isDisabled || undefined}
                            disabled={isDisabled}
                            title={opt.title}
                            onMouseEnter={() => { if (!isDisabled) setActiveIndex(i); }}
                            onClick={() => { if (!isDisabled) onPick(opt.value); }}
                            className={[
                                "flex w-full items-center justify-between gap-[8px] rounded-[2px] border px-[8px] py-[5px] text-left transition-colors",
                                isDisabled ? "cursor-not-allowed opacity-40" : "cursor-pointer",
                                itemClass,
                                isDisabled
                                    ? "border-transparent text-[color:var(--muted-soft)]"
                                    : isSelected
                                        ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)] font-medium"
                                        : isActive
                                            ? "border-[color:var(--line-strong)] text-[color:var(--ink)]"
                                            : "border-transparent text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                            ].join(" ")}
                        >
                            <span className="truncate">{opt.label}</span>
                            <span
                                aria-hidden="true"
                                className={[
                                    "h-[6px] w-[6px] flex-none rounded-[999px] transition-colors",
                                    isSelected ? "bg-[color:var(--green)]" : "bg-transparent",
                                ].join(" ")}
                            />
                        </button>
                    );
                })}
            </div>
        </div>,
        document.body,
    );
}
